import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect
} from 'react-router-dom';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import BannerPortalContext from './BannerPortalContext';
import BrandHeader from './BrandHeader';
import AppFooter from './AppFooter';
import BrandFooter from './BrandFooter';
import Products from '../containers/ProductContainer';
import Permissions from '../containers/PermissionContainer';
import Channels from '../containers/ChannelContainer';
import Login from '../containers/LoginContainer';
import ScrollToTop from '../components/ScrollToTop';
import { protect } from '../containers/AuthContainer';
import SlugHandler from '../containers/SlugContainer';
import { StringProvider } from '../lib/strings';
import { ShowProvider } from '../lib/show';
import { ToastContainer } from './Toast';
import { MaintenancePage } from './MaintenancePage';

const Layout = styled.div`
  ${(props) => props.theme.type.base};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  background-color: ${(props) => props.theme.colours.bg};
  box-sizing: border-box;
  min-height: 100vh;
  a {
    ${(props) => props.theme.type.link};
  }
`;

const View = styled.div`
  max-width: 1024px;
  background: ${(props) => props.theme.colours.bgContrast};
  width: 100%;
  margin: 0 auto;
  flex: 1;
  box-sizing: border-box;
  clear: both;
`;

const App = ({ brand, theme, version, session, underMaintenance=false }) => {
  const [bannerTarget, setBannerTarget] = React.useState(null);

  return (
    <div className={brand}>
      <Router>
        <ThemeProvider theme={theme}>
          <StringProvider strings={theme.strings}>
            <ShowProvider show={theme.show}>
              <BannerPortalContext.Provider value={bannerTarget}>
                <Layout brand={brand}>
                  <BrandHeader brand={brand} theme={theme} session={session} />
                  <div ref={setBannerTarget} />
                  <ScrollToTop />
                  <View className="view-container" >
                    {underMaintenance
                      ? <MaintenancePage brand={brand} />
                      : (
                        <Switch>
                          <Route
                            exact
                            path="/"
                            render={({ location }) => (
                              <Redirect to={{ pathname: '/products', search: location.search }} />
                            )}
                          />
                          <Route exact path="/login" component={Login} />
                          <Route exact path="/products" component={protect(Products)} />
                          <Route exact path="/permission" component={protect(Products)} />
                          <Route
                            exact
                            path="/products/:prodId"
                            component={protect(Permissions)}
                          />
                          <Route
                            exact
                            path="/products/:prodId/:permId"
                            component={protect(Channels)}
                          />
                          <Route
                            exact
                            path="/permission/:permSlug"
                            component={protect(SlugHandler)}
                          />
                          <Route
                            path="/permission"
                            component={protect(Products)}
                          />
                          <Redirect to="/" />
                        </Switch>)
                    }
                    <AppFooter version={version} />
                  </View>
                  <ToastContainer
                    position="bottom-center"
                    autoClose={4000}
                    closeButton={false}
                  />

                </Layout>
              </BannerPortalContext.Provider>
            </ShowProvider>
          </StringProvider>
        </ThemeProvider>
      </Router>
      <BrandFooter brand={brand} />

    </div>
  );
};

App.propTypes = {
  session: PropTypes.shape({
    token: PropTypes.string
  }).isRequired,
  theme: PropTypes.shape({
    show: PropTypes.object,
    palette: PropTypes.object,
    colours: PropTypes.object,
    whitespace: PropTypes.object,
    type: PropTypes.object,
    icons: PropTypes.object,
    assets: PropTypes.object,
    strings: PropTypes.object
  }).isRequired,
  version: PropTypes.string.isRequired,
  brand: PropTypes.string.isRequired,
  underMaintenance: PropTypes.bool
};

export default App;
